import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Modal, Button, Label, TextInput, Select, Alert } from 'flowbite-react';
import { HiLocationMarker } from 'react-icons/hi';
import type { PersonAddressResponse, PersonAddressCreate, PersonAddressUpdate } from '../../types/api';
import { dateInputBounds, validateDateFields } from '../../utils/dateValidation';

// US States for dropdown
const US_STATES = [
  { value: 'PA', label: 'Pennsylvania' },
  { value: 'AL', label: 'Alabama' },
  { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' },
  { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' },
  { value: 'DE', label: 'Delaware' },
  { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' },
  { value: 'HI', label: 'Hawaii' },
  { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' },
  { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' },
  { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' },
  { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' },
  { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' },
  { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' },
  { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' },
  { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' },
  { value: 'OH', label: 'Ohio' },
  { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' },
  { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' },
  { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' },
  { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' },
  { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' },
  { value: 'WY', label: 'Wyoming' },
  { value: 'DC', label: 'District of Columbia' },
];

// Nominatim response structure
interface NominatimResult {
  place_id: number;
  lat: string;
  lon: string;
  display_name: string;
  address: {
    house_number?: string;
    road?: string;
    city?: string;
    town?: string;
    village?: string;
    county?: string;
    state?: string;
    postcode?: string;
    country?: string;
  };
}

interface EditPersonAddressModalProps {
  isOpen: boolean;
  onClose: () => void;
  address?: PersonAddressResponse | null;  // null for new address
  onSave: (addressData: PersonAddressCreate | PersonAddressUpdate) => Promise<void>;
}

export const EditPersonAddressModal: React.FC<EditPersonAddressModalProps> = ({
  isOpen,
  onClose,
  address,
  onSave
}) => {
  const [formData, setFormData] = useState({
    streetNumberName: '',
    apartmentRoomSuiteNumber: '',
    city: '',
    stateAbbreviation: 'PA',
    postalCode: '',
    nameOfCounty: '',
    latitude: null as number | null,
    longitude: null as number | null,
    beginDate: '',
    endDate: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Autocomplete state
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState<NominatimResult[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  // Initialize form data when modal opens or address changes
  useEffect(() => {
    if (address) {
      setFormData({
        streetNumberName: address.streetNumberName || '',
        apartmentRoomSuiteNumber: address.apartmentRoomSuiteNumber || '',
        city: address.city || '',
        stateAbbreviation: address.stateAbbreviation || 'PA',
        postalCode: address.postalCode || '',
        nameOfCounty: address.nameOfCounty || '',
        latitude: address.latitude,
        longitude: address.longitude,
        beginDate: address.beginDate || '',
        endDate: address.endDate || ''
      });
      setSearchQuery(address.streetNumberName || '');
    } else {
      // Reset for new address - default beginDate to today
      const today = new Date().toISOString().split('T')[0];
      setFormData({
        streetNumberName: '',
        apartmentRoomSuiteNumber: '',
        city: '',
        stateAbbreviation: 'PA',
        postalCode: '',
        nameOfCounty: '',
        latitude: null,
        longitude: null,
        beginDate: today,
        endDate: ''
      });
      setSearchQuery('');
    }
    setSuggestions([]);
    setShowSuggestions(false);
  }, [address, isOpen]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounced Nominatim search
  const searchAddress = useCallback(async (query: string) => {
    if (query.length < 5) {
      setSuggestions([]);
      return;
    }

    setSearchLoading(true);
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?` +
        `q=${encodeURIComponent(query)}&format=json&addressdetails=1&countrycodes=us&limit=5`,
        {
          headers: {
            'User-Agent': 'AIU-Directory/1.0 (https://aiu3.net)',
            'Accept': 'application/json'
          }
        }
      );

      if (response.ok) {
        const data: NominatimResult[] = await response.json();
        setSuggestions(data);
        setShowSuggestions(data.length > 0);
      }
    } catch (err) {
      console.error('Nominatim search error:', err);
    } finally {
      setSearchLoading(false);
    }
  }, []);

  // Handle street address input with debounced search
  const handleStreetAddressChange = (value: string) => {
    setSearchQuery(value);
    setFormData({ ...formData, streetNumberName: value });

    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Debounce search (300ms minimum per Nominatim ToS)
    searchTimeoutRef.current = setTimeout(() => {
      searchAddress(value);
    }, 350);
  };

  // Map state name to abbreviation
  const getStateAbbreviation = (stateName: string): string => {
    const state = US_STATES.find(s =>
      s.label.toLowerCase() === stateName.toLowerCase()
    );
    return state?.value || 'PA';
  };

  // Handle selection from autocomplete
  const handleSelectSuggestion = (suggestion: NominatimResult) => {
    const addr = suggestion.address;

    // Build street address from house number and road
    const streetParts = [addr.house_number, addr.road].filter(Boolean);
    const streetAddress = streetParts.join(' ');

    // Get city (could be city, town, or village in Nominatim)
    const city = addr.city || addr.town || addr.village || '';

    // Get state abbreviation
    const stateAbbr = addr.state ? getStateAbbreviation(addr.state) : 'PA';

    // Extract county name (remove " County" suffix if present)
    let county = addr.county || '';
    if (county.toLowerCase().endsWith(' county')) {
      county = county.slice(0, -7);
    }

    setFormData({
      ...formData,
      streetNumberName: streetAddress,
      city: city,
      stateAbbreviation: stateAbbr,
      postalCode: addr.postcode || '',
      nameOfCounty: county,
      latitude: parseFloat(suggestion.lat),
      longitude: parseFloat(suggestion.lon)
    });
    setSearchQuery(streetAddress);
    setShowSuggestions(false);
    setSuggestions([]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    // Validate required fields
    if (!formData.streetNumberName.trim()) {
      setError('Street address is required');
      setLoading(false);
      return;
    }

    if (!formData.city.trim()) {
      setError('City is required');
      setLoading(false);
      return;
    }

    if (!formData.postalCode.trim()) {
      setError('ZIP code is required');
      setLoading(false);
      return;
    }

    // Validate ZIP code format
    const zipRegex = /^\d{5}(-\d{4})?$/;
    if (!zipRegex.test(formData.postalCode.trim())) {
      setError('Please enter a valid ZIP code (e.g., 15237 or 15237-1234)');
      setLoading(false);
      return;
    }

    // Validate date entry (a two-digit year in a native date input reads as 0025)
    const dateError = validateDateFields([
      { value: formData.beginDate, label: 'Begin Date' },
      { value: formData.endDate, label: 'End Date' },
    ]);
    if (dateError) {
      setError(dateError);
      setLoading(false);
      return;
    }

    // Validate date logic
    if (formData.beginDate && formData.endDate) {
      if (new Date(formData.endDate) < new Date(formData.beginDate)) {
        setError('End date cannot be before begin date');
        setLoading(false);
        return;
      }
    }

    try {
      const submitData = {
        streetNumberName: formData.streetNumberName.trim(),
        apartmentRoomSuiteNumber: formData.apartmentRoomSuiteNumber.trim() || null,
        city: formData.city.trim(),
        stateAbbreviation: formData.stateAbbreviation,
        postalCode: formData.postalCode.trim(),
        nameOfCounty: formData.nameOfCounty.trim() || null,
        latitude: formData.latitude,
        longitude: formData.longitude,
        beginDate: formData.beginDate || null,
        endDate: formData.endDate || null
      };

      await onSave(submitData);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save address');
    } finally {
      setLoading(false);
    }
  };

  const isEditMode = !!address;

  return (
    <Modal show={isOpen} onClose={onClose} dismissible size="lg">
      <div className="p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
            <HiLocationMarker className="w-6 h-6 text-purple-600" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900">
            {isEditMode ? 'Edit Address' : 'Add Address'}
          </h3>
        </div>

        {error && (
          <Alert color="failure" onDismiss={() => setError(null)} className="mb-4">
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Street Address with Autocomplete */}
          <div className="relative" ref={suggestionsRef}>
            <Label htmlFor="streetAddress" className="block mb-2 text-sm font-medium text-gray-700">
              Street Address <span className="text-red-500">*</span>
            </Label>
            <TextInput
              id="streetAddress"
              type="text"
              placeholder="Start typing an address..."
              value={searchQuery}
              onChange={(e) => handleStreetAddressChange(e.target.value)}
              onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
              required
              disabled={loading}
              autoComplete="off"
            />
            {searchLoading && (
              <div className="absolute right-3 top-9 text-gray-400 text-sm">
                Searching...
              </div>
            )}

            {/* Autocomplete Suggestions Dropdown */}
            {showSuggestions && suggestions.length > 0 && (
              <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                {suggestions.map((suggestion) => (
                  <button
                    key={suggestion.place_id}
                    type="button"
                    className="w-full px-4 py-3 text-left hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                    onClick={() => handleSelectSuggestion(suggestion)}
                  >
                    <div className="text-sm text-gray-900 font-medium">
                      {[suggestion.address.house_number, suggestion.address.road].filter(Boolean).join(' ')}
                    </div>
                    <div className="text-xs text-gray-500">
                      {[
                        suggestion.address.city || suggestion.address.town || suggestion.address.village,
                        suggestion.address.state,
                        suggestion.address.postcode
                      ].filter(Boolean).join(', ')}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Apt/Suite */}
          <div>
            <Label htmlFor="apt" className="block mb-2 text-sm font-medium text-gray-700">
              Apt/Suite/Unit
            </Label>
            <TextInput
              id="apt"
              type="text"
              placeholder="Apt 2B, Suite 100, etc."
              value={formData.apartmentRoomSuiteNumber}
              onChange={(e) => setFormData({ ...formData, apartmentRoomSuiteNumber: e.target.value })}
              disabled={loading}
            />
          </div>

          {/* City, State, ZIP */}
          <div className="grid grid-cols-6 gap-4">
            <div className="col-span-3">
              <Label htmlFor="city" className="block mb-2 text-sm font-medium text-gray-700">
                City <span className="text-red-500">*</span>
              </Label>
              <TextInput
                id="city"
                type="text"
                placeholder="Pittsburgh"
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                required
                disabled={loading}
              />
            </div>

            <div className="col-span-1">
              <Label htmlFor="state" className="block mb-2 text-sm font-medium text-gray-700">
                State
              </Label>
              <Select
                id="state"
                value={formData.stateAbbreviation}
                onChange={(e) => setFormData({ ...formData, stateAbbreviation: e.target.value })}
                disabled={loading}
              >
                {US_STATES.map(state => (
                  <option key={state.value} value={state.value}>
                    {state.value}
                  </option>
                ))}
              </Select>
            </div>

            <div className="col-span-2">
              <Label htmlFor="postalCode" className="block mb-2 text-sm font-medium text-gray-700">
                ZIP Code <span className="text-red-500">*</span>
              </Label>
              <TextInput
                id="postalCode"
                type="text"
                placeholder="15237"
                value={formData.postalCode}
                onChange={(e) => setFormData({ ...formData, postalCode: e.target.value })}
                required
                disabled={loading}
              />
            </div>
          </div>

          {/* County */}
          <div>
            <Label htmlFor="county" className="block mb-2 text-sm font-medium text-gray-700">
              County
            </Label>
            <TextInput
              id="county"
              type="text"
              placeholder="Allegheny"
              value={formData.nameOfCounty}
              onChange={(e) => setFormData({ ...formData, nameOfCounty: e.target.value })}
              disabled={loading}
            />
          </div>

          {/* Date Range */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="beginDate" className="block mb-2 text-sm font-medium text-gray-700">
                Begin Date
              </Label>
              <TextInput
                id="beginDate"
                type="date"
                value={formData.beginDate}
                onChange={(e) => setFormData({ ...formData, beginDate: e.target.value })}
                disabled={loading}
                {...dateInputBounds}
              />
              <p className="mt-1 text-xs text-gray-500">When this address became valid</p>
            </div>

            <div>
              <Label htmlFor="endDate" className="block mb-2 text-sm font-medium text-gray-700">
                End Date
              </Label>
              <TextInput
                id="endDate"
                type="date"
                value={formData.endDate}
                onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                disabled={loading}
                {...dateInputBounds}
              />
              <p className="mt-1 text-xs text-gray-500">Leave blank if current address</p>
            </div>
          </div>

          {/* Lat/Lon Info */}
          {formData.latitude && formData.longitude && (
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-500">
                Coordinates: {formData.latitude.toFixed(6)}, {formData.longitude.toFixed(6)}
              </p>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button color="gray" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Saving...' : isEditMode ? 'Save Changes' : 'Add Address'}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
};
